import React from 'react';
import { SiteDimension, StoreSiteType, StoreSiteSection } from '../../../types/site';

interface SiteFormData {
  alias: string;
  type_id: number | undefined;
  site_section_id: number | undefined;
  description: string;
  dimension: SiteDimension;
  max_length: number | undefined;
  seasonal: boolean;
}

interface SiteBasicInfoFormProps {
  formData: SiteFormData;
  siteTypes: StoreSiteType[];
  sections: StoreSiteSection[];
  onChange: (updates: Partial<SiteFormData>) => void;
}

const SiteBasicInfoForm: React.FC<SiteBasicInfoFormProps> = ({
  formData,
  siteTypes,
  sections,
  onChange
}) => {
  const handleDimensionChange = (field: keyof SiteDimension, value: any) => {
    onChange({
      dimension: {
        ...formData.dimension,
        [field]: value
      }
    });
  };

  return (
    <div className="site-basic-info-form">
      {/* Alias and Type Row */}
      <div className="form-row">
        <div className="form-group">
          <label>
            Site Alias <span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.alias}
            onChange={(e) => onChange({ alias: e.target.value })}
            placeholder="e.g., F1, W2, RV-101"
            maxLength={20}
          />
          <span className="form-hint">Short identifier for the site (max 20 characters)</span>
        </div>

        <div className="form-group">
          <label>
            Type Name <span className="required">*</span>
          </label>
          <select
            value={formData.type_id || ''}
            onChange={(e) => onChange({ type_id: e.target.value ? Number(e.target.value) : undefined })}
          >
            <option value="">Select a Type Name...</option>
            {siteTypes.filter(t => t.active).map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Section */}
      <div className="form-group">
        <label>Section</label>
        <select
          value={formData.site_section_id || ''}
          onChange={(e) => onChange({ site_section_id: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">No section assigned</option>
          {sections.filter(s => s.active).map(section => (
            <option key={section.id} value={section.id}>
              {section.name}
            </option>
          ))}
        </select>
        <span className="form-hint">Group this site with others in the same area</span>
      </div>

      {/* Description */}
      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Optional notes about this site..."
          rows={3}
        />
      </div>

      {/* Dimensions Section */}
      <div className="form-group">
        <label>Site Dimensions</label>
        <div className="dimension-fields">
          <div className="form-group">
            <label>Unit</label>
            <select
              value={formData.dimension.unit}
              onChange={(e) => handleDimensionChange('unit', e.target.value)}
            >
              <option value="feet">Feet</option>
              <option value="meters">Meters</option>
            </select>
          </div>
          <div className="form-group">
            <label>Length</label>
            <input
              type="number"
              value={formData.dimension.length || ''}
              onChange={(e) => handleDimensionChange('length', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="0"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Width</label>
            <input
              type="number"
              value={formData.dimension.width || ''}
              onChange={(e) => handleDimensionChange('width', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="0"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Height</label>
            <input
              type="number"
              value={formData.dimension.height || ''}
              onChange={(e) => handleDimensionChange('height', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="0"
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Max RV Length */}
      <div className="form-group">
        <label>Max RV/Vehicle Length ({formData.dimension.unit})</label>
        <input
          type="number"
          value={formData.max_length || ''}
          onChange={(e) => onChange({ max_length: e.target.value ? Number(e.target.value) : undefined })}
          placeholder="Maximum length allowed"
          min="0"
        />
        <span className="form-hint">Maximum vehicle length this site can accommodate</span>
      </div>

      {/* Seasonal Toggle */}
      <div className="toggle-row">
        <div className="toggle-label">
          <span>Seasonal Site</span>
          <span>Mark this site for seasonal/long-term rentals only</span>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={formData.seasonal}
            onChange={(e) => onChange({ seasonal: e.target.checked })}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
    </div>
  );
};

export default SiteBasicInfoForm;
